// Libraries
import I18n from 'i18next'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import React, { Component, Fragment } from 'react'
import { bindActionCreators } from 'redux'
import { message, Modal } from 'antd'
import { get, includes, defaultTo, isNil } from 'lodash'

// Ressources
import { actions as cleaningEquipmentActions } from 'Resources/CleaningEquipmentsResource'

// Common components
import { ManageModal } from 'Components'

// Specs
import { ModalSpecs } from './Specs'

// Écrans
import { List } from './Screens'
import { formatOrderedData } from 'Helpers'

const { confirm } = Modal

class CleaningEquipmentConstructor extends Component {
  static propTypes = {
    // Actions d'API
    actions: PropTypes.object,

    // Données de la vue
    equipment: PropTypes.object,
    equipments: PropTypes.array,

    // Témoin d'activité API
    isGathering: PropTypes.bool,
    isUpdating: PropTypes.bool,
    isCreating: PropTypes.bool,

    // Navigation interne
    navigation: PropTypes.shape({
      navigate: PropTypes.func.isRequired,
      getParam: PropTypes.func.isRequired,
      setParams: PropTypes.func.isRequired
    })
  }

  static defaultProps = {
    // Actions d'API
    actions: {},

    // Données de la vue
    equipment: {},
    equipments: [],

    // Témoin d'activité API
    isGathering: false,
    isUpdating: false,
    isCreating: false
  }

  constructor(props) {
    super(props)

    this.state = {
      openedModal: null
    }
  }

  componentDidMount = () => {
    // Récupération des données des éléments de la vue
    this.fetchEquipmentsData()

    if (this.getContext() === 'single') {
      this.getEquipmentData().then(this.askUpdateEquipment)
    }
  }

  /**
   * Récupération du contexte actuel du constructeur de page
   */
  getContext = () => {
    return isNil(this.props.navigation.getParam('id')) ? 'list' : 'single'
  }

  /**
   * Récupération des données des éléments de la vue
   */
  fetchEquipmentsData = () => {
    this.props.actions.fetchCleaningEquipments().catch(() => {
      message.error(I18n.t('api.errors.equipment.fetch'))
    })
  }

  /**
   * Récupération des données détaillées d'un élément de la vue
   */
  getEquipmentData = (equipment = this.props.navigation.getParam('id')) =>
    new Promise((resolve, reject) => {
      this.props.actions
        .getCleaningEquipment(equipment)
        .then(response => {
          // Détails récupérés
          resolve(get(response, 'body'), response)
        })
        .catch(error => {
          // Erreur lors du refresh
          reject(error)
          message.error(I18n.t('api.errors.equipment.get'))
        })
    })

  /**
   * Actions groupées:
   * Archivage de plusieurs lignes du tableau
   */
  archiveManyEquipment = (equipment, clearTableSelections) => {
    this.props.actions
      .archiveManyCleaningEquipment(equipment)
      .then(() => {
        clearTableSelections()
        message.success(I18n.t('api.success.equipment.bulk.archive'))
      })
      .catch(() => {
        message.error(I18n.t('api.errors.equipment.bulk.archive'))
      })
  }

  /**
   * Archivage d'une ligne du tableau
   */
  archiveEquipment = equipment => {
    this.props.actions
      .archiveCleaningEquipment(equipment)
      .then(() => {
        this.safelyCloseModal()
        this.props.navigation.setParams({ id: null })
        message.success(I18n.t('api.success.equipment.archive'))
      })
      .catch(() => {
        message.error(I18n.t('api.errors.equipment.archive'))
      })
  }

  /**
   * Actions groupées:
   * Suppression de plusieurs lignes du tableau
   */
  deleteManyEquipment = (equipment, clearTableSelections) => {
    this.props.actions
      .archiveCleaningEquipments(equipment)
      .then(() => {
        clearTableSelections()
        message.success(I18n.t('api.success.equipment.bulk.delete'))
      })
      .catch(() => {
        message.error(I18n.t('api.errors.equipment.bulk.delete'))
      })
  }

  /**
   * Suppression d'une ligne du tableau
   */
  deleteEquipment = equipment => {
    this.props.actions
      .archiveCleaningEquipment(equipment)
      .then(() => {
        this.safelyCloseModal()
        this.props.navigation.setParams({ id: null })
        message.success(I18n.t('api.success.equipment.delete'))
      })
      .catch(() => {
        message.error(I18n.t('api.errors.equipment.delete'))
      })
  }

  /**
   * Mise à jour d'un equipement
   */
  updateEquipment = (updatedEquipment, originalEquipment) =>
    new Promise((resolve, reject) => {
      this.props.actions
        .updateCleaningEquipment({
          id: get(originalEquipment, 'id'),
          ...updatedEquipment
        })
        .then(response => {
          this.safelyCloseModal()
          this.fetchEquipmentsData()
          resolve(get(response, 'body'), response)
          message.success(I18n.t('api.success.equipment.update'))
        })
        .catch(error => {
          reject(error)
          message.error(I18n.t('api.errors.equipment.update'))
        })
    })

  /**
   * Création d'un equipment
   */
  createEquipment = createdEquipment =>
    new Promise((resolve, reject) => {
      this.props.actions
        .createCleaningEquipment(createdEquipment)
        .then(response => {
          this.safelyCloseModal()
          resolve(get(response, 'body'), response)
          message.success(I18n.t('api.success.equipment.create'))
        })
        .catch(error => {
          reject(error)
          message.error(I18n.t('api.errors.equipment.create'))
        })
    })

  /**
   * Actions groupées:
   * Demande d'archivage de plusieurs lignes du tableau
   */
  askArchiveManyEquipments = (...params) => {
    confirm({
      title: I18n.t('pages.equipment.modal.archive.multiple.title'),
      content: I18n.t('pages.equipment.modal.archive.multiple.content'),
      okText: I18n.t('pages.equipment.modal.archive.multiple.okText'),
      okType: 'danger',
      cancelText: I18n.t('pages.equipment.modal.archive.multiple.cancelText'),
      onOk: () => {
        this.archiveEquipment(...params)
      },
      maskClosable: true
    })
  }

  /**
   * Demande d'archivage d'une ligne du tableau
   */
  askArchiveEquipment = () => {
    const { equipment } = this.props

    confirm({
      title: I18n.t('pages.equipment.modal.archive.single.title'),
      content: I18n.t('pages.equipment.modal.archive.single.content'),
      okText: I18n.t('pages.equipment.modal.archive.single.okText'),
      okType: 'danger',
      cancelText: I18n.t('pages.equipment.modal.archive.single.cancelText'),
      onOk: () => {
        this.archiveEquipment(equipment)
      },
      maskClosable: true
    })
  }

  /**
   * Actions groupées:
   * Demande d'archivage de plusieurs lignes du tableau
   */
  askDeleteManyEquipments = (...params) => {
    confirm({
      title: I18n.t('pages.equipment.modal.delete.multiple.title'),
      content: I18n.t('pages.equipment.modal.delete.multiple.content'),
      okText: I18n.t('pages.equipment.modal.delete.multiple.okText'),
      okType: 'danger',
      cancelText: I18n.t('pages.equipment.modal.delete.multiple.cancelText'),
      onOk: () => {
        this.deleteManyEquipment(...params)
      },
      maskClosable: true
    })
  }

  /**
   * Demande d'archivage d'une ligne du tableau
   */
  askDeleteEquipment = () => {
    const { equipment } = this.props

    confirm({
      title: I18n.t('pages.equipment.modal.delete.single.title'),
      content: I18n.t('pages.equipment.modal.delete.single.content'),
      okText: I18n.t('pages.equipment.modal.delete.single.okText'),
      okType: 'danger',
      cancelText: I18n.t('pages.equipment.modal.delete.single.cancelText'),
      onOk: () => {
        this.deleteEquipment(equipment)
      },
      maskClosable: true
    })
  }

  /**
   * Demande de modification d'une ligne du tableau
   */
  askUpdateEquipment = () => {
    this.setState({ openedModal: 'updateEquipment' })
  }

  /**
   * Demande de création d'une ligne du tableau
   */
  askCreateEquipment = () => {
    this.setState({ openedModal: 'createEquipment' })
  }

  /**
   * Fermeture de toutes les modales
   */
  safelyCloseModal = () => {
    const { openedModal } = this.state

    if (openedModal === 'updateEquipment') {
      this.setState({ openedModal: 'transition' }, () => {
        setTimeout(
          this.setState({ openedModal: null }, () => {
            this.props.navigation.setParams({ id: null })
          }),
          200
        )
      })
    } else {
      this.setState({ openedModal: null })
    }
  }

  /**
   * Callback:
   * Création d'une ligne de tableau réussie
   */
  handleRowCreated = () => {
    this.safelyCloseModal()
  }

  /**
   * Callback:
   * Modification d'une ligne de tableau réussie
   */
  handleRowUpdated = () => {
    this.safelyCloseModal()
  }

  /**
   * Clic sur une ligne du tableau
   */
  handleRowClick = equipment => {
    // Navigation vers la page détaillée
    this.props.navigation.setParams({ id: get(equipment, 'id') })

    this.getEquipmentData(equipment)
      .then(() =>
        // Ouverture de la modale
        this.askUpdateEquipment()
      )
      .catch(() => {
        this.props.navigation.setParams({ id: null })
      })
  }

  handleDraggedRowReleased = orderedData => {
    const formattedOrderedData = formatOrderedData(orderedData)

    this.props.actions
      .orderCleaningEquipment(formattedOrderedData)
      .then(() => message.success(I18n.t('api.success.equipment.order')))
      .catch(() => message.error(I18n.t('api.errors.equipment.order')))
  }

  /**
   * Formatage des lignes de la modale de modification / création
   */
  formatModalRows = rows => {
    return rows
  }

  render() {
    // Récupération des éléments des props
    let {
      isGathering,
      isUpdating,
      isCreating,
      equipments,
      equipment
    } = this.props

    // Récupération des éléments du state
    const { openedModal } = this.state

    // Chargement
    const loading = isGathering || isUpdating || isCreating

    // Création de la liste des fonctions constructeur exposés au écrans
    const constructorFunctions = {
      // Clic sur un ligne de tableau
      handleRowClick: this.handleRowClick,

      // Gestion de la modale
      safelyCloseModal: this.safelyCloseModal,

      // Récupération des données détaillées d'une ligne du tableau
      getEquipmentData: this.getEquipmentData,

      // Création d'une ligne de tableau
      handleRowCreated: this.handleRowCreated,
      askCreateEquipment: this.askCreateEquipment,

      // Modification d'une ou plusieurs ligne(s) de tableau
      handleRowUpdated: this.handleRowUpdated,
      askUpdateEquipment: this.askUpdateEquipment,
      askUpdateManyEquipments: this.askUpdateEquipment,

      handleDraggedRowReleased: this.handleDraggedRowReleased,

      // Archivage d'une ou plusieurs ligne(s) du tableau
      askArchiveEquipment: this.askArchiveEquipment,
      askArchiveManyEquipments: this.askArchiveManyEquipments,

      // Suppression d'une ou plusieurs ligne(s) du tableau
      askDeleteEquipment: this.askDeleteEquipment,
      askDeleteManyEquipments: this.askDeleteManyEquipments
    }

    // Ajout des actions sur la modale
    ModalSpecs.actions.delete.onClick = this.askDeleteEquipment

    // Ajout des lignes au formulaire
    // formLayout = merge(formLayout, manageModal)

    return (
      <Fragment>
        {/* Rendu de l'écran approprié au contexte du constructeur */}
        <List
          loading={loading}
          constructorFunctions={constructorFunctions}
          constructorState={this.state}
          dataList={equipments}
        />

        {/* Ouverture de la modale de création / modification */}
        <ManageModal
          width={800}
          loading={loading}
          onCancel={this.safelyCloseModal}
          onOk={this.handleRowCreated}
          onChange={
            openedModal === 'updateEquipment'
              ? this.updateEquipment
              : this.createEquipment
          }
          visible={includes(
            ['createEquipment', 'updateEquipment'],
            openedModal
          )}
          data={
            includes(['transition', 'updateEquipment'], openedModal)
              ? equipment
              : null
          }
          {...ModalSpecs}
        />
      </Fragment>
    )
  }
}

const mapStateToProps = state => {
  const defaultProps = get(CleaningEquipmentConstructor, 'defaultProps', {})

  return {
    equipments: defaultTo(
      get(state, 'cleaningEquipment.items'),
      defaultProps.equipments
    ),
    equipment: defaultTo(
      get(state, 'cleaningEquipment.item'),
      defaultProps.equipment
    ),
    isGathering:
      defaultTo(
        get(state, 'cleaningEquipment.isFetching'),
        defaultProps.isGathering
      ) ||
      defaultTo(
        get(state, 'cleaningEquipment.isFetchingItem'),
        defaultProps.isGathering
      ),
    isUpdating: defaultTo(
      get(state, 'cleaningEquipment.isUpdating'),
      defaultProps.isUpdating
    )
  }
}

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ ...cleaningEquipmentActions }, dispatch)
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CleaningEquipmentConstructor)
